/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Per-provider gegevensvereisten voor een aanmelding — and what each of them
 * costs. ONE module for three surfaces that must never disagree:
 *
 *   1. the priced list (admin-owned, `provider_crm.json` →
 *      `leadSettings.requiredItems`) — what we charge for,
 *   2. the question list (price-free) that rides the public payloads and
 *      drives the wizard,
 *   3. the published dossier fact "Nodig om aan te melden", DERIVED from (1)
 *      rather than proposed by the provider.
 *
 * Plain ESM JavaScript on purpose (the signupLeads.js / dossier.js pattern):
 * consumed by the client bundle (wizard, portal, admin panel), the server
 * (CRM validation, the confirm endpoint, POST /api/leads) AND
 * scripts/prerender.mjs, which cannot import TypeScript. Keep it
 * dependency-free — no crypto here; an id minter is INJECTED.
 *
 * Three rules shaped this module:
 *
 *   - **A price nobody typed never bills.** There is no implicit default at
 *     read time: an unset base price reads as `null` and every total derived
 *     from it is `null` too. `LEAD_ITEM_DEFAULT_PRICE_EUR` is a UI prefill,
 *     never a fallback.
 *   - **Custom-item ids are stable.** They are minted once, server-side, and
 *     round-tripped by the admin UI. Id churn would change the terms
 *     signature on every save and void every provider confirmation.
 *   - **The signature is what staleness is judged on.** Labels count (the
 *     provider agreed to a named item); hints do not (editorial wording).
 *     A snapshot taken before items existed signs as base-only, so shipping
 *     this feature does not make every existing confirmation stale.
 */

// The derived published fact is built with the dossier model's own validator,
// so the entry we derive passes exactly the rule every other dossier entry
// passes — an invalid derived entry must disappear, never render as half a fact.
import { normalizeDossierValue, publicDossier, DOSSIER_FIELDS } from './dossier.js';

/** Every collectable item kind. `custom` LAST — it is also the sort order. */
export const LEAD_ITEM_KINDS = /** @type {const} */ ([
  'serial',
  // A photo of the typeplaatje — the product-information sticker on the
  // laadpaal (2026-09-30). Next to `serial` because both are read off the same
  // sticker and both are asked on the wizard's charger step.
  'type_plate_photo',
  'meter_reading',
  'meter_photo',
  'kvk',
  'energy_contract',
  'custom',
]);

/** Base price per doorgestuurde aanmelding (excl. btw). INCLUDES the EAN. */
export const LEAD_BASE_PRICE_EUR = 15;
/** UI PREFILL for a newly ticked item — never a read-time fallback. */
export const LEAD_ITEM_DEFAULT_PRICE_EUR = 2.5;
export const MAX_CUSTOM_LEAD_ITEMS = 3;
/** Array cap on the stored list (6 fixed kinds minus the meter pair + 3 customs). */
export const MAX_LEAD_ITEMS = 8;
export const LEAD_ITEM_LABEL_MAX = 60;
export const LEAD_ITEM_HINT_MAX = 140;
/** A visitor's free-text answer to a custom item. */
export const LEAD_ITEM_ANSWER_MAX = 200;
/** Typo guard on an item price, mirroring MAX_PRICE_EUR in the CRM route. */
export const LEAD_ITEM_PRICE_MAX = 10000;
export const ENERGY_SUPPLIER_MAX = 100;
/**
 * THE cap on ONE uploaded photo (the meterstand, the typeplaatje), after the
 * client downscale. One number: the browser refuses to hand over a result above
 * it, the API refuses a decoded buffer above it, and the express body tier
 * below is derived FROM it — so a photo the wizard accepted can never come back
 * as a bare 413.
 */
export const MAX_PHOTO_BYTES = Math.floor(1.5 * 1024 * 1024);
/**
 * The kinds that are answered with a photo. One aanmelding carries at most one
 * photo per kind, so the length of this list is also how many photos one body
 * can hold — which is what the body tier below multiplies by.
 */
export const LEAD_PHOTO_ITEM_KINDS = /** @type {const} */ (['type_plate_photo', 'meter_photo']);
/**
 * The express JSON limit for POST /api/leads. Base64 is 4/3 of the bytes, and
 * the rest of the body (contact, adres, EAN, aanbod, comment, Turnstile token)
 * fits many times over in 256 KB. Derived, never typed twice: a raise of
 * MAX_PHOTO_BYTES — or a third photo kind — that forgot this number would turn
 * every large photo into a body-parser 413 instead of a field-level refusal.
 */
export const LEAD_BODY_LIMIT_BYTES =
  LEAD_PHOTO_ITEM_KINDS.length * Math.ceil((MAX_PHOTO_BYTES * 4) / 3) + 256 * 1024;

/**
 * Kinds that only apply to ONE audience. A KvK-nummer is not asked for and
 * not charged on a particulier aanmelding (decision E4) — `leadItemsForAudience`
 * is the single place that rule lives, client and server alike.
 * @type {Readonly<Record<string, string>>}
 */
export const LEAD_ITEM_AUDIENCE = Object.freeze({ kvk: 'Zakelijk' });

/**
 * Kinds an admin may mark `optional`: the form ASKS, the visitor may skip, and
 * the surcharge bills only on an aanmelding that actually brought the item.
 * Only the typeplaatje photo today — a visitor who is not standing next to the
 * laadpaal cannot take one, and a required field they cannot fill is a lost
 * aanmelding (the reason the serienummer is optional by default). Every other
 * kind is required whenever it is listed; `normalizeLeadItems` refuses the flag
 * on them rather than storing a term no surface honours.
 */
export const LEAD_ITEM_OPTIONAL_KINDS = /** @type {const} */ (['type_plate_photo']);
const OPTIONAL_KINDS = new Set(/** @type {readonly string[]} */ (LEAD_ITEM_OPTIONAL_KINDS));

/**
 * Kinds asked on the wizard's CHARGER step rather than on the extras step: they
 * belong with the laadpaal. A provider that asks for nothing else adds no step.
 */
export const LEAD_CHARGER_STEP_KINDS = /** @type {const} */ (['serial', 'type_plate_photo']);
const CHARGER_STEP_KINDS = new Set(/** @type {readonly string[]} */ (LEAD_CHARGER_STEP_KINDS));

/**
 * Short Dutch names for the fixed kinds — ONE table for the admin inbox, the
 * CRM panel and the two lead mails, all three of which are Dutch-only internal
 * surfaces. `custom` is absent on purpose: its label is admin-typed DATA and
 * renders verbatim, never through a lookup.
 * @type {Readonly<Record<string, string>>}
 */
export const LEAD_ITEM_LABELS_NL = Object.freeze({
  serial: 'serienummer',
  type_plate_photo: 'foto van het typeplaatje',
  meter_reading: 'meterstand',
  meter_photo: 'meterfoto',
  kvk: 'KvK-nummer',
  energy_contract: 'energieleverancier',
});

/**
 * A meterstand as the two Dutch-only readers print it: the admin inbox and both
 * lead mails, which must never spell the same number differently.
 *
 * It lives HERE and not in src/lib/formatters.ts because that module type-
 * imports `Language` from the React LanguageContext, which the server's own tsc
 * project cannot compile — and `formatDecimal` pins minimumFractionDigits too,
 * which would print a meterstand typed as "12345" as "12.345,000 kWh".
 *
 * @param {number} value  kWh
 * @returns {string}
 */
export function formatMeterReadingNl(value) {
  const n = finiteOrNull(value);
  if (n === null) return '';
  return new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 3 }).format(n);
}

/**
 * kind → the `signupRequirements` dossier code the derived public fact uses.
 * Two kinds share `meter_reading`: whether we ask for the number or a photo is
 * an implementation detail of the form, not a different fact about the deal.
 * @type {Readonly<Record<string, string>>}
 */
export const LEAD_ITEM_DOSSIER_CODE = Object.freeze({
  serial: 'serial_number',
  type_plate_photo: 'type_plate_photo',
  meter_reading: 'meter_reading',
  meter_photo: 'meter_reading',
  kvk: 'kvk',
  energy_contract: 'energy_contract',
  custom: 'other',
});

/** The shape a minted custom id must have — pinned so a round-trip is verifiable. */
export const LEAD_ITEM_ID_RE = /^itm_[a-z0-9]{6,12}$/;

/**
 * @typedef {'serial'|'type_plate_photo'|'meter_reading'|'meter_photo'|'kvk'|'energy_contract'|'custom'} LeadItemKind
 *
 * @typedef {Object} LeadRequiredItem   the PRICED list — admin-realm only
 * @property {LeadItemKind} kind
 * @property {number} priceEur          surcharge on top of the base price
 * @property {boolean} [optional]       only ever `true`, only on a kind in
 *                                      LEAD_ITEM_OPTIONAL_KINDS: asked, may be
 *                                      skipped, billed only when brought
 * @property {string} [id]              custom items only; stable, minted once
 * @property {string} [label]           custom items only
 * @property {string} [hint]            custom items only, optional
 *
 * @typedef {Object} LeadPublicItem     the QUESTION list — no prices, ever
 * @property {LeadItemKind} kind
 * @property {boolean} [optional]
 * @property {string} [id]
 * @property {string} [label]
 * @property {string} [hint]
 *
 * @typedef {Object} LeadConfirmedItem  one item as a confirmation snapshots it
 * @property {LeadItemKind} kind
 * @property {number} priceEur
 * @property {boolean} [optional]       on a terms snapshot only — a billed row
 *                                      on a lead never carries it
 * @property {string} [id]
 * @property {string} [label]
 *
 * @typedef {Object} LeadPriceTotals    what one aanmelding costs, per audience
 * @property {number|null} totalEur     base + the items EVERY aanmelding brings
 *                                      (the particulier price); null when the
 *                                      base price is unset
 * @property {number|null} businessTotalEur  base + ALL required items, but only
 *                                      when an audience-only item exists; else null
 * @property {boolean} hasAudienceOnlyItem
 * @property {number|null} optionalEur  what the OPTIONAL items add on an
 *                                      aanmelding that brings them; null when
 *                                      there are none. Never part of a total.
 *
 * @typedef {Object} LeadTerms          the live commercial terms of one provider
 * @property {number|null} basePriceEur
 * @property {LeadRequiredItem[]} items
 * @property {number|null} totalEur     see LeadPriceTotals
 * @property {number|null} businessTotalEur
 * @property {boolean} hasAudienceOnlyItem
 * @property {number|null} optionalEur
 *
 * @typedef {Object} LeadSettingsLike   the raw leadSettings block, as read
 * @property {unknown} [priceEur]
 * @property {unknown} [requiredItems]
 * @property {unknown} [requiredItemsUpdatedAt]
 */

/**
 * The place of each kind in LEAD_ITEM_KINDS, asked with any string.
 *
 * @type {ReadonlyMap<string, number>}
 */
const KIND_INDEX = new Map(LEAD_ITEM_KINDS.map((k, i) => [k, i]));
/** Cents, never a float tail. Exported because every surface that adds two of
 *  these amounts must round the same way. @param {number} n @returns {number} */
export const round2 = (n) => Math.round(n * 100) / 100;
/** @param {string} s */
const collapse = (s) => String(s).replace(/\s+/g, ' ').trim();

/** @param {unknown} v @returns {number|null} */
function finiteOrNull(v) {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/** @param {unknown} raw @returns {Set<string>} */
function toKeySet(raw) {
  if (raw instanceof Set) return raw;
  return new Set(Array.isArray(raw) ? raw.map((k) => String(k)) : []);
}

/**
 * The stable key of one item — what a collected answer is filed under and what
 * `missingLeadItems` compares. A custom item is only ever addressed by its id,
 * so a relabelled custom keeps its answer and a new custom never inherits one.
 *
 * @param {{ kind: string, id?: string }} item
 * @returns {string}
 */
export function leadItemKey(item) {
  const kind = item && typeof item === 'object' ? String(item.kind ?? '') : '';
  if (kind !== 'custom') return kind;
  return `custom:${item.id ?? ''}`;
}

/**
 * THE validation rule for a submitted `requiredItems` list — the admin CRM
 * PATCH is the only writer, and it goes through here.
 *
 * Refusals (never silent repairs — an admin who mistyped a price must see it):
 * not an array, more than MAX_LEAD_ITEMS entries, an unknown kind, a missing
 * or out-of-range price, a repeated non-custom kind, `meter_reading` together
 * with `meter_photo` (decision E3: the two are mutually exclusive; a "both"
 * wish is a custom item), a custom without a label, an over-long label or
 * hint, more than MAX_CUSTOM_LEAD_ITEMS customs, an `optional` that is not a
 * boolean, or `optional: true` on a kind outside LEAD_ITEM_OPTIONAL_KINDS.
 *
 * A custom id is KEPT when it already has the minted shape and minted
 * otherwise, so the admin UI round-trips ids and a confirmation survives an
 * unrelated edit. Without a `mintId` (a pure client-side preview) a custom
 * that has no valid id is a refusal rather than a silently id-less row.
 *
 * Output is in kind order with customs last, so two saves of the same list
 * produce the same array — and therefore the same signature.
 *
 * Optional-fields result rather than a discriminated union, for the reason
 * validateLeadSettingsInput gives (a shape from before tsconfig.server was
 * strict, when a truthiness check did not narrow union members).
 *
 * @param {unknown} raw
 * @param {{ mintId?: () => string }} [options]
 * @returns {{ ok: boolean, items?: LeadRequiredItem[], error?: string }}
 */
export function normalizeLeadItems(raw, options = {}) {
  if (!Array.isArray(raw)) return { ok: false, error: 'requiredItems must be an array' };
  if (raw.length > MAX_LEAD_ITEMS) {
    return { ok: false, error: `requiredItems: at most ${MAX_LEAD_ITEMS} items` };
  }
  const mintId = typeof options.mintId === 'function' ? options.mintId : null;
  /** @type {LeadRequiredItem[]} */
  const out = [];
  const seenKinds = new Set();
  const seenIds = new Set();
  let customs = 0;

  for (const entry of raw) {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      return { ok: false, error: 'requiredItems: each entry must be an object' };
    }
    const e = /** @type {Record<string, unknown>} */ (entry);
    const kind = typeof e.kind === 'string' ? e.kind : '';
    if (!KIND_INDEX.has(kind)) {
      return { ok: false, error: `requiredItems: unknown kind "${kind}"` };
    }
    const price = finiteOrNull(e.priceEur);
    if (price === null || price < 0 || price > LEAD_ITEM_PRICE_MAX) {
      return {
        ok: false,
        error: `requiredItems: ${kind} needs a priceEur between 0 and ${LEAD_ITEM_PRICE_MAX}`,
      };
    }
    /** @type {LeadRequiredItem} */
    const item = { kind: /** @type {LeadItemKind} */ (kind), priceEur: round2(price) };
    if (e.optional !== undefined && typeof e.optional !== 'boolean') {
      return { ok: false, error: `requiredItems: ${kind}.optional must be a boolean` };
    }
    if (e.optional === true) {
      if (!OPTIONAL_KINDS.has(kind)) {
        return { ok: false, error: `requiredItems: ${kind} cannot be optional` };
      }
      // Stored only when true: a required item keeps the exact shape it had
      // before the flag existed, and so does its terms signature.
      item.optional = true;
    }

    if (kind === 'custom') {
      customs += 1;
      if (customs > MAX_CUSTOM_LEAD_ITEMS) {
        return { ok: false, error: `requiredItems: at most ${MAX_CUSTOM_LEAD_ITEMS} custom items` };
      }
      const label = typeof e.label === 'string' ? collapse(e.label) : '';
      if (!label) return { ok: false, error: 'requiredItems: a custom item needs a label' };
      if (label.length > LEAD_ITEM_LABEL_MAX) {
        return {
          ok: false,
          error: `requiredItems: a label is at most ${LEAD_ITEM_LABEL_MAX} characters`,
        };
      }
      const hint = typeof e.hint === 'string' ? collapse(e.hint) : '';
      if (hint.length > LEAD_ITEM_HINT_MAX) {
        return {
          ok: false,
          error: `requiredItems: a hint is at most ${LEAD_ITEM_HINT_MAX} characters`,
        };
      }
      const given = typeof e.id === 'string' ? e.id : '';
      let id;
      if (LEAD_ITEM_ID_RE.test(given)) {
        if (seenIds.has(given)) return { ok: false, error: 'requiredItems: duplicate custom id' };
        id = given;
      } else if (mintId) {
        id = mintId();
      } else {
        return { ok: false, error: 'requiredItems: a custom item needs an id' };
      }
      seenIds.add(id);
      item.id = id;
      item.label = label;
      if (hint) item.hint = hint;
    } else {
      if (seenKinds.has(kind)) {
        return { ok: false, error: `requiredItems: ${kind} is listed twice` };
      }
      seenKinds.add(kind);
    }
    out.push(item);
  }

  if (seenKinds.has('meter_reading') && seenKinds.has('meter_photo')) {
    return {
      ok: false,
      error: 'requiredItems: meter_reading and meter_photo are mutually exclusive',
    };
  }

  // Every kind in `out` passed KIND_INDEX.has above.
  out.sort(
    (a, b) =>
      /** @type {number} */ (KIND_INDEX.get(a.kind)) -
      /** @type {number} */ (KIND_INDEX.get(b.kind))
  );
  return { ok: true, items: out };
}

/**
 * Tolerant READ of a stored or wire-borne list — the counterpart of the strict
 * writer above (the sanitizeCommercialAgreements idiom). A hand-edited file, a
 * page baked before a kind existed or a stale service-worker cache must never
 * take a surface down, so a row this version cannot understand is dropped
 * instead of throwing.
 *
 * ONE loop for the priced and the price-free read, because the two must agree
 * on every rule that is not the price. They disagreed on the HINT: the public
 * read kept one on a fixed kind, which `normalizeLeadItems` never writes (a
 * hint is editorial wording on an admin-typed question), so the wizard could
 * render a hint no admin could ever have entered.
 *
 * @param {unknown} raw
 * @param {boolean} priced  true = a row without a usable price is DROPPED (a
 *                          price nobody typed may not become €0 on an invoice
 *                          line); false = prices are not read at all, so this
 *                          can never make a client believe it knows a price.
 * @returns {LeadRequiredItem[]}
 */
function sanitizeItemList(raw, priced) {
  if (!Array.isArray(raw)) return [];
  /** @type {LeadRequiredItem[]} */
  const out = [];
  const seenKinds = new Set();
  const seenIds = new Set();
  let customs = 0;
  for (const entry of raw) {
    if (out.length >= MAX_LEAD_ITEMS) break;
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) continue;
    const e = /** @type {Record<string, unknown>} */ (entry);
    const kind = typeof e.kind === 'string' ? e.kind : '';
    if (!KIND_INDEX.has(kind)) continue;
    /** @type {LeadRequiredItem} */
    const item = /** @type {LeadRequiredItem} */ ({ kind: /** @type {LeadItemKind} */ (kind) });
    if (priced) {
      const price = finiteOrNull(e.priceEur);
      if (price === null || price < 0 || price > LEAD_ITEM_PRICE_MAX) continue;
      item.priceEur = round2(price);
    }
    if (e.optional === true && OPTIONAL_KINDS.has(kind)) item.optional = true;
    if (kind === 'custom') {
      if (customs >= MAX_CUSTOM_LEAD_ITEMS) continue;
      const id = typeof e.id === 'string' ? e.id : '';
      if (!LEAD_ITEM_ID_RE.test(id) || seenIds.has(id)) continue;
      const label =
        typeof e.label === 'string' ? collapse(e.label).slice(0, LEAD_ITEM_LABEL_MAX) : '';
      if (!label) continue;
      const hint = typeof e.hint === 'string' ? collapse(e.hint).slice(0, LEAD_ITEM_HINT_MAX) : '';
      seenIds.add(id);
      customs += 1;
      item.id = id;
      item.label = label;
      if (hint) item.hint = hint;
    } else {
      if (seenKinds.has(kind)) continue;
      // First of the mutually exclusive pair wins — never both.
      if (kind === 'meter_photo' && seenKinds.has('meter_reading')) continue;
      if (kind === 'meter_reading' && seenKinds.has('meter_photo')) continue;
      seenKinds.add(kind);
    }
    out.push(item);
  }
  // Every kind in `out` passed KIND_INDEX.has above.
  out.sort(
    (a, b) =>
      /** @type {number} */ (KIND_INDEX.get(a.kind)) -
      /** @type {number} */ (KIND_INDEX.get(b.kind))
  );
  return out;
}

/**
 * The PRICED list as it is stored — admin realm only.
 * @param {unknown} raw
 * @returns {LeadRequiredItem[]}
 */
export function sanitizeLeadItems(raw) {
  return sanitizeItemList(raw, true);
}

/**
 * A PUBLIC `signupItems` payload as the wizard reads it back. The wire is data,
 * not a contract, and the form must degrade to "ask nothing extra" rather than
 * render a question it does not understand.
 * @param {unknown} raw
 * @returns {LeadPublicItem[]}
 */
export function sanitizeLeadPublicItems(raw) {
  return /** @type {LeadPublicItem[]} */ (sanitizeItemList(raw, false));
}

/**
 * Has an admin ever configured the list for this provider? `[]` (explicitly
 * base-only) and an ABSENT key mean different things: the first says "we ask
 * for nothing extra", the second says "nobody has decided yet" — only the
 * second falls back to the legacy dossier fact.
 *
 * @param {LeadSettingsLike | null | undefined} ls
 * @returns {boolean}
 */
export function hasConfiguredLeadItems(ls) {
  return Array.isArray(ls && ls.requiredItems);
}

/**
 * What one aanmelding costs, per audience. ONE arithmetic for the portal card,
 * the admin panel, the drawer and the stored terms snapshot.
 *
 * Decision E4 is the whole reason there are two numbers: a KvK-nummer is not
 * asked for and not charged on a particulier aanmelding, so the HEADLINE total
 * is the particulier one (`leadItemsForAudience(items, null)` — the same rule
 * the server prices a lead with) and the zakelijk figure is a second line that
 * only exists when an audience-only item does. `null` in (no base price
 * recorded) is `null` out: a price nobody typed never becomes a number.
 *
 * An OPTIONAL item is in neither total: a total is what an aanmelding costs
 * whatever the visitor does, and an optional item bills only when it came
 * along. Its amount is reported separately as `optionalEur`, so a surface can
 * say "+ € 2,50 when the photo is included" instead of quoting a price that is
 * wrong for every aanmelding without one.
 *
 * @param {unknown} basePriceEur
 * @param {readonly LeadRequiredItem[] | readonly LeadConfirmedItem[] | null | undefined} items
 * @returns {LeadPriceTotals}
 */
export function leadPriceTotals(basePriceEur, items) {
  const base = finiteOrNull(basePriceEur);
  const list = Array.isArray(items) ? items : [];
  const required = list.filter((i) => !(i && i.optional));
  const optional = list.filter((i) => !!(i && i.optional));
  const hasAudienceOnlyItem = required.some((i) => !!(i && LEAD_ITEM_AUDIENCE[i.kind]));
  /** @param {readonly (LeadRequiredItem | LeadConfirmedItem)[]} rows */
  const sum = (rows) => rows.reduce((n, i) => n + (finiteOrNull(i && i.priceEur) ?? 0), 0);
  return {
    totalEur: base === null ? null : round2(base + sum(leadItemsForAudience(required, null))),
    businessTotalEur: base === null || !hasAudienceOnlyItem ? null : round2(base + sum(required)),
    hasAudienceOnlyItem,
    optionalEur: optional.length === 0 ? null : round2(sum(optional)),
  };
}

/**
 * The live commercial terms of one provider, from its raw leadSettings.
 * `basePriceEur` is deliberately NOT defaulted to LEAD_BASE_PRICE_EUR: a
 * provider whose price nobody recorded has no price, and the delivery gate
 * says so rather than billing a number nobody agreed to.
 *
 * The totals are CONVENIENCE, not terms: `leadTermsSignature` reads the base
 * price and the items only, so adding, renaming or recomputing a total can
 * never make a provider's click-confirmation stale.
 *
 * @param {LeadSettingsLike | null | undefined} ls
 * @returns {LeadTerms}
 */
export function leadTermsFor(ls) {
  const basePriceEur = finiteOrNull(ls && ls.priceEur);
  const items = sanitizeLeadItems(ls && ls.requiredItems);
  return { basePriceEur, items, ...leadPriceTotals(basePriceEur, items) };
}

/**
 * The items that apply to ONE aanmelding. `targetAudience` is the resolved
 * audience of the chosen aanbod; `null` (a generic CTA, or no aanbod at all)
 * reads as particulier — never as "ask for everything".
 *
 * @template {{ kind: string }} T
 * @param {readonly T[] | null | undefined} items
 * @param {string | null | undefined} targetAudience
 * @returns {T[]}
 */
export function leadItemsForAudience(items, targetAudience) {
  if (!Array.isArray(items)) return [];
  const audience = typeof targetAudience === 'string' ? targetAudience : null;
  return items.filter((item) => {
    const only = LEAD_ITEM_AUDIENCE[item && item.kind];
    return !only || only === audience;
  });
}

/**
 * Which required items this submission did NOT bring. The caller turns the
 * first one into a `missing_item` refusal naming the field, so a visitor is
 * sent back to the row rather than told "something is wrong". An OPTIONAL item
 * is never missing — skipping it is what the flag allows.
 *
 * @template {{ kind: string, id?: string }} T
 * @param {readonly T[] | null | undefined} items
 * @param {string | null | undefined} targetAudience
 * @param {Iterable<string> | Set<string> | null | undefined} collectedKeys
 * @returns {T[]}
 */
export function missingLeadItems(items, targetAudience, collectedKeys) {
  const keys = toKeySet(collectedKeys);
  return leadItemsForAudience(items, targetAudience).filter(
    (item) =>
      !(/** @type {{ optional?: boolean }} */ (item).optional) && !keys.has(leadItemKey(item))
  );
}

/**
 * The price snapshot stamped onto a lead at creation — ONLY the items that
 * were actually collected, so a KvK item never bills on a particulier lead,
 * an optional item the visitor skipped never bills at all, and an item added
 * after this lead was captured never bills retroactively.
 *
 * Deviation from the plan's argument name: the first argument is the whole
 * `LeadTerms` (from `leadTermsFor`), not a bare item array — the return shape
 * carries `basePriceEur`, which a bare array cannot supply.
 *
 * `totalEur === null` (no base price recorded) is a legitimate outcome: the
 * form may be in capture-only test mode, where nothing is delivered and
 * nothing is billed. Delivery is gated on the base price separately.
 *
 * @param {LeadTerms | null | undefined} terms
 * @param {string | null | undefined} targetAudience
 * @param {Iterable<string> | Set<string> | null | undefined} collectedKeys
 * @returns {{ basePriceEur: number|null, items: LeadConfirmedItem[], totalEur: number|null }}
 */
export function itemPriceForLead(terms, targetAudience, collectedKeys) {
  const basePriceEur = finiteOrNull(terms && terms.basePriceEur);
  const keys = toKeySet(collectedKeys);
  const items = leadItemsForAudience(terms && terms.items, targetAudience)
    .filter((item) => keys.has(leadItemKey(item)))
    // A billed row is a billed row: whether the item COULD have been skipped
    // is a term of the afspraak, not a fact about this aanmelding.
    .map((item) => toConfirmedItem(item, false));
  const totalEur =
    basePriceEur === null ? null : round2(items.reduce((sum, i) => sum + i.priceEur, basePriceEur));
  return { basePriceEur, items, totalEur };
}

/**
 * @param {LeadRequiredItem} item
 * @param {boolean} [keepOptional]  true for a terms snapshot, false for a billed row
 * @returns {LeadConfirmedItem}
 */
function toConfirmedItem(item, keepOptional = true) {
  /** @type {LeadConfirmedItem} */
  const out = { kind: item.kind, priceEur: item.priceEur };
  if (keepOptional && item.optional) out.optional = true;
  if (item.id) out.id = item.id;
  if (item.label) out.label = item.label;
  return out;
}

/**
 * The priced list as a confirmation snapshots it: kind, id, label, price and
 * whether the item may be skipped — no hint. The hint is editorial wording of
 * the same question, and rewording it must not cost the provider a
 * re-confirmation.
 *
 * @param {readonly LeadRequiredItem[] | null | undefined} items
 * @returns {LeadConfirmedItem[]}
 */
export function leadItemsToConfirmed(items) {
  return Array.isArray(items) ? items.map((item) => toConfirmedItem(item)) : [];
}

/**
 * The fingerprint the provider's click-confirmation is judged against. Tokens
 * are SORTED, so reordering the list is not a terms change; the label is IN
 * (the provider agreed to a named item) and the hint is OUT. `optional` is IN
 * as a trailing marker that only an optional item carries — "always billed" and
 * "billed when brought" are different terms, and a required item signs exactly
 * as it did before the flag existed.
 *
 * Accepts both shapes that exist: a live `LeadTerms` (`basePriceEur`) and a
 * stored `LeadAgreementTerms` snapshot (`priceEur` = the base, possibly with
 * no `items` at all). A snapshot taken before items existed therefore signs
 * as base-only and stays `confirmed` against a live provider that has no
 * items — shipping this feature re-nudges nobody.
 *
 * NEVER hand it a `LeadDelivery`: there `priceEur` means the invoiced TOTAL and
 * the item rows live under `priceItems.items`, so the result would sign a wrong
 * base with no item tokens at all. Terms in, never a delivery row.
 *
 * @param {{ basePriceEur?: unknown, priceEur?: unknown, items?: unknown } | null | undefined} terms
 * @returns {string}
 */
export function leadTermsSignature(terms) {
  const t = terms && typeof terms === 'object' ? terms : {};
  const base = finiteOrNull(t.basePriceEur) ?? finiteOrNull(t.priceEur);
  const tokens = sanitizeSignatureItems(t.items)
    .map(
      (i) =>
        `${i.kind}:${i.id ?? ''}:${i.priceEur.toFixed(2)}:${i.label ?? ''}${i.optional ? ':optional' : ''}`
    )
    .sort();
  return [`v1`, `base:${base === null ? 'none' : base.toFixed(2)}`, ...tokens].join('|');
}

/**
 * Signature input is deliberately laxer than sanitizeLeadItems: a snapshot is
 * history and must sign as it was stored, including a row a later version of
 * the rules would drop. Only a shapeless row is skipped.
 * @param {unknown} raw
 * @returns {LeadConfirmedItem[]}
 */
function sanitizeSignatureItems(raw) {
  if (!Array.isArray(raw)) return [];
  /** @type {LeadConfirmedItem[]} */
  const out = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) continue;
    const e = /** @type {Record<string, unknown>} */ (entry);
    if (typeof e.kind !== 'string' || !e.kind) continue;
    const price = finiteOrNull(e.priceEur);
    /** @type {LeadConfirmedItem} */
    const item = {
      kind: /** @type {LeadItemKind} */ (e.kind),
      priceEur: price === null ? 0 : round2(price),
    };
    if (e.optional === true) item.optional = true;
    if (typeof e.id === 'string' && e.id) item.id = e.id;
    if (typeof e.label === 'string' && e.label) item.label = e.label;
    out.push(item);
  }
  return out;
}

/**
 * The priced list stripped of prices — the ONLY shape that may ride a public
 * payload. Commercial terms never leave the admin realm.
 *
 * @param {readonly (LeadRequiredItem|LeadPublicItem)[] | null | undefined} items
 * @returns {LeadPublicItem[]}
 */
export function leadItemsToPublic(items) {
  if (!Array.isArray(items)) return [];
  return items.map((item) => {
    /** @type {LeadPublicItem} */
    const out = { kind: item.kind };
    if (item.optional) out.optional = true;
    if (item.id) out.id = item.id;
    if (item.label) out.label = item.label;
    if (item.hint) out.hint = item.hint;
    return out;
  });
}

/**
 * "Nodig om aan te melden", DERIVED from the question list (decision E1). The
 * EAN is only claimed when the form is actually live for this provider: a
 * provider without a form does not collect an EAN through us. An OPTIONAL item
 * is left out: the fact is what a visitor NEEDS, and nobody needs an item the
 * form lets them skip.
 *
 * Returns `null` when there is nothing truthful to publish, so the caller
 * removes the fact instead of rendering an empty list.
 *
 * @param {readonly LeadPublicItem[] | null | undefined} publicItems
 * @param {{ formActive?: boolean, source?: string, at?: unknown }} context  `at` is
 *   used when it is a non-empty string (the stored stamp, read as it is)
 * @returns {{ value: string[], amount: null, note: string, source: string, at: string|null } | null}
 */
export function leadSignupDossierEntry(publicItems, context = {}) {
  const codes = [];
  const labels = [];
  if (context.formActive) codes.push('ean');
  for (const item of Array.isArray(publicItems) ? publicItems : []) {
    if (item && item.optional) continue;
    const code = LEAD_ITEM_DOSSIER_CODE[item && item.kind];
    if (!code) continue;
    if (!codes.includes(code)) codes.push(code);
    if (item.kind === 'custom' && item.label) labels.push(item.label);
  }
  if (codes.length === 0) return null;
  // `other` without a word of explanation is a shrug — normalizeDossierValue
  // refuses it, and so does this: the custom labels ARE that explanation.
  const note = labels.join(', ');
  const normalized = normalizeDossierValue('signupRequirements', { value: codes, note });
  if (!normalized) return null;
  return {
    // signupRequirements is a multi field that takes no amount: its value is
    // always a code list and its amount null.
    .../** @type {{ value: string[], amount: null, note: string }} */ (normalized),
    source: context.source === 'provider' ? 'provider' : 'editorial',
    at: typeof context.at === 'string' && context.at ? context.at : null,
  };
}

/**
 * Put the derived entry into a public dossier (or take it out when there is
 * none), rebuilt in DOSSIER_FIELDS order so both mirrors emit byte-identical
 * payloads. `null` out when nothing remains — the renderer drops the block.
 *
 * @param {Record<string, unknown> | null | undefined} dossier
 * @param {unknown} entry
 * @returns {Record<string, unknown> | null}
 */
export function withSignupRequirements(dossier, entry) {
  const base = dossier && typeof dossier === 'object' ? dossier : {};
  /** @type {Record<string, unknown>} */
  const out = {};
  for (const field of DOSSIER_FIELDS) {
    if (field === 'signupRequirements') {
      if (entry) out[field] = entry;
      continue;
    }
    if (base[field]) out[field] = base[field];
  }
  return Object.keys(out).length ? out : null;
}

/**
 * The two gegevensvereisten facts every public surface publishes for ONE
 * provider — composed HERE so the four builders (`/api/providers`,
 * `GET /api/provider-profile/:slug` and both builders in
 * scripts/prerender.mjs) cannot drift apart. Before this they each carried the
 * same nine lines, and a fix had to land four times or not at all.
 *
 *   signupItems — the price-free QUESTION list the wizard reads. `[]` while
 *     nobody configured one: the form must never ask for an item nobody priced.
 *   dossier — `publicDossier` plus "Nodig om aan te melden".
 *
 * The fallback is what "read-time" means literally: while the list is NOT
 * configured the provider's OWN stored entry is published verbatim — the same
 * bytes the page showed yesterday, `ean` and `mid_certificate` included. It is
 * only once an admin configures the list that the fact becomes DERIVED (and
 * disappears when nothing derivable is left). Deriving the fallback too would
 * quietly rewrite 100+ published pages the day this shipped.
 *
 * @param {{ dossier?: unknown } | null | undefined} provider
 * @param {LeadSettingsLike | null | undefined} leadSettings
 * @param {boolean} formActive  is the aanmeldformulier live for this provider
 * @returns {{ signupItems: LeadPublicItem[], dossier: Record<string, unknown> | null }}
 */
export function publicSignupFacts(provider, leadSettings, formActive) {
  const signupItems = leadItemsToPublic(
    sanitizeLeadItems(leadSettings && leadSettings.requiredItems)
  );
  const base = publicDossier(provider);
  if (!hasConfiguredLeadItems(leadSettings)) return { signupItems, dossier: base };
  return {
    signupItems,
    dossier: withSignupRequirements(
      base,
      leadSignupDossierEntry(signupItems, {
        formActive: !!formActive,
        source: 'editorial',
        at: leadSettings && leadSettings.requiredItemsUpdatedAt,
      })
    ),
  };
}

// ── Wizard shape ────────────────────────────────────────────────────────────
// Pure, so the step list is computed identically by the wizard, its tests and
// anything that needs to know whether a provider adds a step.

/**
 * Does this provider need the extra-questions step? The serienummer and the
 * typeplaatje photo are asked on the CHARGER step (they belong with the
 * laadpaal), so a provider that asks only for those adds no step at all.
 *
 * @param {readonly {kind: string}[] | null | undefined} items
 * @param {string | null | undefined} targetAudience
 * @returns {boolean}
 */
export function hasExtrasStep(items, targetAudience) {
  return leadItemsForAudience(items, targetAudience).some(
    (i) => i && !CHARGER_STEP_KINDS.has(i.kind)
  );
}

/**
 * The wizard's step order for one provider — derived, never a constant, since
 * 2026-09-16.
 *
 * @param {readonly {kind: string}[] | null | undefined} items
 * @param {string | null | undefined} targetAudience
 * @returns {string[]}
 */
export function signupStepsFor(items, targetAudience) {
  const steps = ['contact', 'address', 'charger'];
  if (hasExtrasStep(items, targetAudience)) steps.push('extras');
  steps.push('review');
  return steps;
}

/**
 * "a, b en c" — the consent sentence enumerates what we pass on, and it has to
 * read like a sentence in both languages. `andWord` comes from i18n and
 * carries its own spaces (` en ` / ` and `); empty parts drop out.
 *
 * @param {readonly (string|null|undefined)[]} parts
 * @param {string} andWord
 * @returns {string}
 */
export function listJoin(parts, andWord) {
  const clean = (Array.isArray(parts) ? parts : []).filter(
    (p) => typeof p === 'string' && p.trim() !== ''
  );
  if (clean.length === 0) return '';
  if (clean.length === 1) return clean[0];
  return `${clean.slice(0, -1).join(', ')}${andWord}${clean[clean.length - 1]}`;
}
